/**
 * The pre-trade checklist — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS IS FOR
 *
 * The thing that loses money on a short-premium book is not usually a wrong
 * strike; it is entering on a day the trader would not have entered on if
 * they had stopped to ask. The checklist is the stop.
 *
 * SO IT IS A SENTENCE, NOT A CONTROL. Nothing here is checked by software.
 * "Global cues checked — no gap risk overnight" cannot be verified by this
 * product and must not pretend to be: a tick box the trader ticks is an
 * honest record of a human decision, and a green tick the system awarded
 * itself would be a claim it cannot support.
 *
 * DEFAULTS ARE PER STRATEGY, BECAUSE THE QUESTIONS ARE.
 *
 * What you check before a Short Straddle (is today a decay day?) has almost
 * nothing in common with what you check before a Call Ratio Spread (is the
 * unlimited upside accepted?). One generic list would be skipped by the
 * second week.
 *
 * AND THE TRADER OWNS THEIRS. These are only the starting point. A saved
 * list on `Strategy.checklist` replaces the default entirely — including
 * with an EMPTY list, which is how a trader turns the gate off for one
 * strategy. Null means "never edited", so improving a default still reaches
 * everyone who has not written their own.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/** Used when a strategy has no list of its own. */
export const DEFAULT_CHECKLIST = [
    'Position size is within my per-trade risk limit',
    'Stop-loss plan decided before entry',
    'No major news or event in the next hour',
    'Margin checked in the broker terminal — enough buffer',
    'I know my exit before I enter',
];
/**
 * Starting checklists, by template key.
 *
 * Deliberately NOT referring to automated rules. Guardian (Phase 7 Stage B)
 * does not exist yet, and a checklist line telling a trader to arm a stop
 * the product cannot arm is worse than no line.
 */
export const STRATEGY_CHECKLISTS = {
    SHORT_STRADDLE: [
        'Today is a decay day — no major event expected',
        'Global cues checked — no gap risk overnight',
        'Stop decided at 40–50% premium rise',
        'Exit time decided — square off or carry',
        'Position size within my per-order lot limit',
    ],
    SHORT_STRANGLE: [
        "The day's expected range is marked",
        'Both short strikes sit beyond that range',
        'Plan ready for the side that gets tested',
        'Margin checked in the broker terminal',
        'Exit time decided — square off or carry',
    ],
    IRON_FLY: [
        'Pin zone near ATM identified',
        'Wings wide enough to survive a 1% move',
        'Max loss is acceptable if the market runs',
        'Stop decided at 40–50% premium rise',
    ],
    IRON_CONDOR: [
        'Premium is worth selling — volatility is not at the floor',
        'No RBI, budget or results event today',
        'Both short strikes are outside the expected move',
        'Max loss is acceptable — it is the wing width minus credit',
        'Exit time decided — square off or carry',
    ],
    BATMAN: [
        'Both tails are armoured — the wings are in the basket',
        'Wing cost is under a third of the premium collected',
        'Max loss acceptable if the wings never pay',
        'Stop decided at 40–50% premium rise',
    ],
    JADE_LIZARD: [
        'Call spread width is under the credit — no upside risk',
        'Comfortable owning the downside at the short put strike',
        'Put premium is rich enough to be worth the risk',
    ],
    BROKEN_WING_BUTTERFLY: [
        'Directional view is written down',
        'The broken side can absorb a fast move',
        'Entry is a credit, or a debit I accept losing',
    ],
    CALL_RATIO_SPREAD: [
        'Upside risk above the short strikes is UNLIMITED — accepted',
        'The rally target matches the short strike',
        'Exit plan ready if spot breaks out',
    ],
    LONG_STRADDLE: [
        'A move is expected that beats the premium paid',
        "The day's theta cost is acceptable",
        'Exit plan ready for a volatility crush',
    ],
    LONG_STRANGLE: [
        'A move is expected that beats the premium paid',
        'Both breakevens are inside the expected range',
        "The day's theta cost is acceptable",
    ],
    BULL_CALL_SPREAD: [
        'The up move is expected before this expiry',
        'The short strike is at or above the target',
        'Debit paid is the whole risk, and it is acceptable',
    ],
    BULL_PUT_SPREAD: [
        'Support below the short strike is identified',
        'Max loss is acceptable — spread width minus credit',
        'Plan ready if the short put is breached',
    ],
    BEAR_CALL_SPREAD: [
        'Resistance above the short strike is identified',
        'Max loss is acceptable — spread width minus credit',
        'Plan ready if the short call is breached',
    ],
    BEAR_PUT_SPREAD: [
        'The down move is expected before this expiry',
        'The short strike is at or below the target',
        'Debit paid is the whole risk, and it is acceptable',
    ],
};
/**
 * The starting list for a template.
 *
 * `Object.hasOwn` for the same reason `templateFor` uses it: keys arrive
 * from saved rows and URLs, and a bare index reaches the prototype.
 */
export function defaultChecklistFor(templateKey) {
    return Object.hasOwn(STRATEGY_CHECKLISTS, templateKey)
        ? STRATEGY_CHECKLISTS[templateKey]
        : DEFAULT_CHECKLIST;
}
/** Longest a single check may be. Long enough for a sentence, not a policy. */
export const CHECKLIST_ITEM_MAX = 160;
/** Most checks one strategy may carry. A list nobody reads is not a gate. */
export const CHECKLIST_MAX_ITEMS = 20;
/**
 * Clean a submitted list, or say why it cannot be stored.
 *
 * Validated HERE rather than in the route so the server and the browser
 * agree on what a checklist is — the browser can refuse a 161st character
 * without a round trip, and the server never trusts that it did.
 */
export function parseChecklist(input) {
    if (!Array.isArray(input))
        return { ok: false, why: 'A checklist is a list of lines.' };
    if (input.length > CHECKLIST_MAX_ITEMS) {
        return { ok: false, why: `A checklist holds at most ${CHECKLIST_MAX_ITEMS} checks.` };
    }
    const items = [];
    for (const raw of input) {
        if (typeof raw !== 'string')
            return { ok: false, why: 'Every check must be a line of text.' };
        const text = raw.trim();
        // A blank line is dropped rather than refused: it is what an empty input
        // box produces, and refusing the whole save for one is unkind.
        if (!text)
            continue;
        if (text.length > CHECKLIST_ITEM_MAX) {
            return { ok: false, why: `A check is at most ${CHECKLIST_ITEM_MAX} characters.` };
        }
        items.push(text);
    }
    return { ok: true, items };
}
//# sourceMappingURL=strategyChecklist.js.map